import {
  AirflowIcon,
  ClaudeIcon,
  CursorIcon,
  DbtIcon,
  JiraIcon,
  SnowflakeCategoryIcon,
  SqlToolsIcon,
  VSCodeIcon,
} from "@/assets/icons";
import { AltimateLogo, BigqueryIcon, DatabricksIcon, PostgresIcon } from "@/assets/images";
import { z } from "zod";

export const InputParameterSchema = z.object({
  key: z.string().optional(),
  name: z.string(),
  type: z.string(),
  description: z.string().optional(),
  defaultValue: z.string().nullish(),
  required: z.boolean(),
  encrypted: z.boolean().optional(),
  options: z.array(z.string()).nullish(),
  // TODO: this is nested in multi levels - handle the types.ts properly
  // same in engine
  items: z
    .object({
      additionalProperties: z.boolean().optional(),
      required: z.array(z.string()).optional(),
      type: z.string().optional(),
      properties: z
        .intersection(
          z.record(z.any()),
          z.record(
            z
              .object({
                type: z.string().optional(),
                items: z
                  .intersection(
                    z.record(z.any()),
                    z.object({
                      additionalProperties: z.boolean().optional(),
                      required: z.array(z.string()).optional(),
                      type: z.string().optional(),
                    })
                  )
                  .optional(),
              })
              .optional()
          )
        )
        .optional(),
    })
    .optional(),
});

export const KeyValueSchema = z.object({
  value: z.unknown(),
  key: z.string(),
});

export type InputParameter = z.infer<typeof InputParameterSchema>;

export type KeyValue = z.infer<typeof KeyValueSchema>;

export enum MCPIntegrationType {
  API = "api",
  CODE = "code",
  EXTENSION = "extension",
  TOOL = "tool",
  MCP = "mcp",
}

export enum SupportedLanguages {
  PYTHON = "python",
}

export const CUSTOM_INTEGRATIONS = [
  {
    name: "Custom Python code",
    type: MCPIntegrationType.CODE,
    description: "Write custom Python code to automate your dbt projects.",
    comingSoon: false,
    configureInputs: true,
    configureConfig: false,
    toolConfig: [
      {
        name: "Language",
        key: "language",
        type: "select",
        options: Object.values(SupportedLanguages),
        required: true,
        defaultValue: SupportedLanguages.PYTHON,
        description: "Select the language of the code",
        schema: z.nativeEnum(SupportedLanguages).describe("Select the language of the code"),
      },
      {
        name: "Code",
        key: "code",
        type: "code",
        required: true,
        description: "Write your custom Python code here",
        schema: z.string().describe("Write your custom Python code here").nonempty(),
      },
    ],
  },
  {
    name: "Custom API",
    type: MCPIntegrationType.API,
    description: "Use your own API to automate your workflows.",
    comingSoon: false,
    configureInputs: true,
    configureConfig: false,
    toolConfig: [
      {
        name: "OpenAPI Specification",
        key: "apiSpec",
        type: "code",
        required: true,
        description: "Paste your OpenAPI specification here (JSON or YAML)",
        schema: z
          .string()
          .describe("Paste your OpenAPI specification here (JSON or YAML)")
          .nonempty(),
      },
    ],
    config: [
      {
        name: "Base URL",
        key: "baseUrl",
        type: "text",
        required: true,
        description: "Enter your base URL",
        schema: z.string().describe("Enter your base URL"),
      },
      {
        name: "Authorization header",
        key: "apiKey",
        type: "text",
        required: true,
        description: "Enter your authorization header",
        schema: z.string().describe("Enter your API key for authentication"),
      },
      {
        name: "Custom headers",
        key: "headers",
        type: "headers",
        required: false,
        description: "Enter your custom headers",
        schema: z
          .array(z.object({ key: z.string(), value: z.string() }))
          .describe("Enter your custom headers"),
      },
    ],
  },
  {
    name: "Custom MCP Server",
    type: MCPIntegrationType.MCP,
    comingSoon: false,
    configureInputs: false,
    configureConfig: true,
    tools: [] as InputParameter[],
    toolConfig: [
      {
        name: "Command",
        key: "command",
        type: "text",
        required: true,
        description: "Enter the command for your MCP server",
        schema: z.string().describe("Enter the command for your MCP server"),
      },
      {
        name: "Arguments",
        key: "arguments",
        type: "multi",
        required: false,
        description: "Enter the arguments for your MCP server",
        schema: z.array(z.string()).describe("Enter the arguments for your MCP server").optional(),
      },
      {
        name: "Environment variables",
        key: "env",
        type: "environmentVariables",
        required: false,
        description: "Enter the environment variables for your MCP server",
        schema: z
          .array(z.object({ key: z.string(), value: z.string() }))
          .describe("Enter the arguments for your MCP server")
          .optional()
          .nullable(),
      },
    ],
    description: "Use your own MCP server to automate your workflows.",
  },
];

// Filter applicable for for custom integrations
export const CustomIntegrationApplicableFor = "mcp_custom_integrations";

// Filter applicable for for selected integrations
export const TeammatesApplicableFor = "teammates";

export enum IntegrationName {
  POWER_USER_FOR_DBT = "power-user-for-dbt",
  SQLTOOLS = "sqltools",
  AIRFLOW = "airflow",
  GITHUB = "github",
  JIRA = "jira",
  ALTIMATE = "altimate",
  SNOWFLAKE = "snowflake",
  BIGQUERY = "bigquery",
  POSTGRESQL = "postgresql",
  DATABRICKS = "databricks",
}
export const IntegrationIcons = {
  [IntegrationName.POWER_USER_FOR_DBT]: <DbtIcon className="al-w-4 al-h-4" />,
  [IntegrationName.SQLTOOLS]: <SqlToolsIcon className="al-w-4 al-h-4" />,
  [IntegrationName.AIRFLOW]: <AirflowIcon className="al-w-4 al-h-4" />,
  [IntegrationName.GITHUB]: <i className="codicon codicon-github-inverted al-w-4 al-h-4" />,
  [IntegrationName.JIRA]: <JiraIcon className="al-w-4 al-h-4" />,
  [IntegrationName.ALTIMATE]: <AltimateLogo className="al-w-4 al-h-4" />,
  [IntegrationName.SNOWFLAKE]: <SnowflakeCategoryIcon className="al-w-4 al-h-4" />,
  [IntegrationName.BIGQUERY]: <BigqueryIcon className="al-w-4 al-h-4" />,
  [IntegrationName.POSTGRESQL]: <img src={PostgresIcon} alt="Postgres" className="al-w-4 al-h-4" />,
  [IntegrationName.DATABRICKS]: <DatabricksIcon className="al-w-4 al-h-4" />,
};

export enum DatamateTemplateType {
  SQL_EXPERT = "sql_expert",
  PIPELINE_MANAGER = "pipeline_manager",
  QUALITY_MONITOR = "quality_monitor",
  CUSTOM = "custom",
}

export const DATAMATE_TEMPLATES = [
  {
    type: DatamateTemplateType.SQL_EXPERT,
    name: "SQL Expert",
    description: "Query optimization and data exploration",
    icon: <SnowflakeCategoryIcon className="al-w-4 al-h-4" />,
    integrations: [IntegrationName.SNOWFLAKE, IntegrationName.ALTIMATE],
  },
  {
    type: DatamateTemplateType.PIPELINE_MANAGER,
    name: "Pipeline Manager",
    description: "Data pipeline monitoring and maintenance",
    icon: <AirflowIcon className="al-w-4 al-h-4" />,
    integrations: [IntegrationName.AIRFLOW, IntegrationName.SNOWFLAKE, IntegrationName.ALTIMATE],
  },
  {
    type: DatamateTemplateType.QUALITY_MONITOR,
    name: "Quality Monitor",
    description: "Data quality and anomaly detection",
    icon: <DbtIcon className="al-w-4 al-h-4" />,
    integrations: [
      IntegrationName.POWER_USER_FOR_DBT,
      IntegrationName.SNOWFLAKE,
      IntegrationName.ALTIMATE,
    ],
  },
];

export const McpClientInterfaces = [
  {
    type: "vscode",
    label: "VS Code",
    icon: <VSCodeIcon className="al-w-4 al-h-4" />,
  },
  {
    type: "cursor",
    label: "Cursor",
    icon: <CursorIcon className="al-w-4 al-h-4" />,
  },
  {
    type: "claude-code",
    label: "Claude Code",
    icon: <ClaudeIcon className="al-w-4 al-h-4" />,
  },
];

export const DatapilotCliMcpOutputSchema = z.object({
  tools: z
    .array(
      z.object({
        name: z.string(),
        description: z.string().nullable(),
        inputSchema: z.object({
          type: z.literal("object"),
          properties: z.record(
            z.object({
              type: z.string(),
              description: z.string(),
              items: z
                .object({
                  additionalProperties: z.boolean(),
                  required: z.array(z.string()).optional(),
                  type: z.string(),
                  properties: z.record(
                    z.object({
                      type: z.string(),
                      description: z.string(),
                      items: z
                        .object({
                          additionalProperties: z.boolean(),
                          required: z.array(z.string()).optional(),
                          type: z.string(),
                        })
                        .optional(),
                    })
                  ),
                })
                .optional(),
            })
          ),
          required: z.array(z.string()).optional(),
        }),
      })
    )
    .nullable(),
});

export type DatapilotCliMcpOutput = z.infer<typeof DatapilotCliMcpOutputSchema>;

export const ConnectionsQueryKey = "connections";
